import PropTypes from "prop-types";

import { useEffect, useState } from "react";
import MoreItemsNav from "../../../MoreItemsNav/component";
import classNames from "classnames";
import NavItem from "../../../NavItem/component";
import { VIEWPORT_MOBILE } from "./constants";
import { useResponsiveBehaviour } from "../../../../hooks/useResponsiveNav";

/**
 * Render a single navigation link
 * @param {object} item The item that needs to be rendered into a Link
 * @param {int} index The index of the link
 * @param {boolean} isVisible Indication on whether the link is visible
 * @returns {void}
 */
export const renderSingleNavItem = (props, item) => {
    const { value, index, isVisible } = item;
    const listItemClasses = classNames("subNavListItem", {
        standAlone: item.standAlone !== undefined,
        hidden: !isVisible,
    });

    const itemProps = {
        item: value,
        index: index,
        className: listItemClasses,
        linkClassName: classNames("subNavLink", { firstItem: index === 0 }),
        path: props.path,
        isExternalHeader: props.isExternalHeader,
    };
    return <NavItem key={`responsive-${index}`} {...itemProps} />;
};

/**
 * Render the "More" menu via the MoreItemsNav component
 * @param {object} hiddenItems The list of nav items to be rendered under the "More" menu
 * @param {bool} allHidden A flag that indicates whether all items are hidden
 * @returns {void}
 */
export const renderHiddenMenu = (
    props,
    hiddenItems,
    hiddenItemsCount,
    allHidden
) => {
    const moreNavProps = {
        items: hiddenItems,
        baseNavSection: props.baseNavSection,
        subSection: props.subSection,
        path: props.path,
        allHidden: allHidden,
        moreButtonLabel: props.moreButtonLabel,
        isExternalHeader: props.isExternalHeader,
        id: props.id + "-more-items-list",
    };

    // Always render the "More" button, but toggle visibility so that it's included in the calculations
    const classes = classNames("subNavListItem", "moreNavTrigger", {
        active: hiddenItemsCount && hiddenItems && hiddenItems.length,
    });

    return (
        <li className={classes}>
            <MoreItemsNav {...moreNavProps} />
        </li>
    );
};

const ResponsiveNav = ({
    items = null,
    baseNavSection = "",
    subSection = "",
    path = "",
    className,
    viewport = "",
    collapseOnMobile = true,
    gemDepartment = null,
    moreButtonLabel = "",
    isExternalHeader = false,
    id = "responsive-nav",
}) => {
    const [overflowHidden, setOverflowHidden] = useState(true);
    const { assignRef, getHiddenItemsCount } = useResponsiveBehaviour(
        items,
        path,
        {
            collapseOnMobile: collapseOnMobile,
        }
    );

    useEffect(() => {
        // Hide the nav bar overflow before the "More" button is rendered
        setOverflowHidden(false);
    }, []);

    const hiddenItemsCount = getHiddenItemsCount();

    let renderNavItems = "";
    const allHidden = viewport === VIEWPORT_MOBILE;
    let hiddenItems = allHidden ? items : null;
    let splitIndex = 0;
    let isVisible = true;

    if (items && !allHidden) {
        splitIndex = items.length - hiddenItemsCount;
        hiddenItems = items.slice(splitIndex, items.length);

        // Render list items
        renderNavItems = items.map((item, index) => {
            if (hiddenItemsCount) {
                isVisible = index < splitIndex ? true : false;
            }
            return renderSingleNavItem(
                { path, isExternalHeader },
                {
                    value: item,
                    index,
                    isVisible,
                }
            );
        });
    }

    const classes = classNames("responsiveNav subNavList", className, {
        allHidden: allHidden || !splitIndex,
        overflowHidden: overflowHidden,
    });

    const gemLink = gemDepartment ? (
        <li className="gemWrapper subNavListItem">{gemDepartment}</li>
    ) : null;

    return (
        <ul
            className={classes}
            ref={(ref) => assignRef(ref)}
            data-testid="responsive-nav-ul"
        >
            {gemLink}
            {renderNavItems}
            {renderHiddenMenu(
                {
                    baseNavSection,
                    subSection,
                    path,
                    moreButtonLabel,
                    isExternalHeader,
                    id,
                },
                hiddenItems,
                hiddenItemsCount,
                allHidden
            )}
        </ul>
    );
};

ResponsiveNav.propTypes = {
    items: PropTypes.array,
    baseNavSection: PropTypes.string,
    subSection: PropTypes.string,
    path: PropTypes.string,
    className: PropTypes.string,
    viewport: PropTypes.string,
    collapseOnMobile: PropTypes.bool,
    gemDepartment: PropTypes.object,
    moreButtonLabel: PropTypes.string,
    isExternalHeader: PropTypes.bool,
    id: PropTypes.string,
};

export default ResponsiveNav;
